import { cn } from "@/modules/lib/utils"
import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import {
  BoxPurchasesCreditModal,
  type BoxPurchasesCreditModalRef,
} from "@modules/boxPurchases/components/boxPurchasesCreditModal"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import {
  BoxSubscriptionDeleteModal,
  type BoxSubscriptionDeleteModalRef,
} from "@modules/boxSubscription/components/boxSubscriptionDeleteModal"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { word } from "@modules/core/utils/i18n"
import { MemberDetailedEntity } from "@modules/members/entities/memberEntity"
import { MemberOfferDiscountForm } from "@modules/members/memberOffers/components/memberOfferDiscountForm"
import { MemberOfferPauseForm } from "@modules/members/memberOffers/components/memberOfferPauseForm"
import { MemberOffersWrapper } from "@modules/members/memberOffers/components/memberOffersWrapper"
import { MemberOffersWrapperSkeleton } from "@modules/members/memberOffers/components/memberOffersWrapperSkeleton"
import errorNoRepLight from "@images/error/errorNoRepLight.svg"
import {
  MemberSendOfferModal,
  type MemberSendOfferModalRef,
} from "@modules/members/sendOfferToMember/components/memberSendOfferModal"
import { Suspense, useRef, useState } from "react"
import { Text } from "@/components/ui/baseUi/text"
import { ErrorBoundary } from "react-error-boundary"

type Props = {
  memberDetailed: MemberDetailedEntity
  className?: string
} & React.ComponentProps<"div">

export function MemberTabOffersLayout({ memberDetailed, className, ...props }: Props) {
  const [isOfferSupensionVisible, setIsOfferSupensionVisible] = useState(false)
  const [isOfferDiscountVisible, setIsOfferDiscountVisible] = useState(false)
  const [subscriptionSelected, setSubscriptionSelected] = useState<BoxSubscriptionDetailsEntity | null>(null)
  const [consumableSelected, setConsumableSelected] = useState<BoxConsumableDetailsEntity | null>(null)

  const boxSubscriptionDeleteModal = useRef<BoxSubscriptionDeleteModalRef>(null)
  const boxPurchasesCreditModal = useRef<BoxPurchasesCreditModalRef>(null)

  const memberSendOfferModal = useRef<MemberSendOfferModalRef>(null)
  const canSendOffer = useBoxStaffPermission(BoxStaffPermissions.SEND_OFFER)

  const handleSendOfferModal = () => {
    memberSendOfferModal.current?.open()
  }

  const handleDeleteSubscription = () => {
    boxSubscriptionDeleteModal.current?.open()
  }

  const handleShowCreditModal = (consumableDetails: BoxConsumableDetailsEntity) => {
    setConsumableSelected(consumableDetails)
    boxPurchasesCreditModal.current?.open()
  }

  return (
    <>
      {isOfferDiscountVisible && subscriptionSelected?.id && (
        <MemberOfferDiscountForm
          memberId={memberDetailed.id}
          subscription={subscriptionSelected}
          setIsOfferDiscountVisible={setIsOfferDiscountVisible}
        />
      )}

      {isOfferSupensionVisible && subscriptionSelected?.id && (
        <MemberOfferPauseForm
          memberId={memberDetailed.id}
          subscription={subscriptionSelected}
          setIsOfferSupensionVisible={setIsOfferSupensionVisible}
        />
      )}

      {isOfferDiscountVisible === false && isOfferSupensionVisible === false && (
        <div className={cn("flex w-full flex-col gap-6 px-4 pt-4 pb-6", className)} {...props}>
          {/* Send offer button */}
          {canSendOffer && (
            <div
              onClick={handleSendOfferModal}
              className="shiny-hover mx-1 flex cursor-pointer items-center justify-center gap-2 rounded-3xl border-2 border-borderPrimary bg-neutral-50 p-2"
            >
              <img src={boxOfferPlanIcon} />
              <Text fontStyle="TS" className="text-textDefault-body">
                {word("box.member.table.offer.menu.sendOffer")}
              </Text>
            </div>
          )}
          <div className="flex w-full border border-solid border-borderDefault" />
          {/* Offers List */}
          <Suspense fallback={<MemberOffersWrapperSkeleton />}>
            <ErrorBoundary fallback={<MemberOffersLayoutError />}>
              <MemberOffersWrapper
                memberId={memberDetailed.id}
                userId={memberDetailed.userId}
                setSubscriptionSelected={setSubscriptionSelected}
                setIsOfferSupensionVisible={setIsOfferSupensionVisible}
                setIsOfferDiscountVisible={setIsOfferDiscountVisible}
                handleDeleteSubscription={handleDeleteSubscription}
                handleShowCreditModal={handleShowCreditModal}
              />
            </ErrorBoundary>
          </Suspense>
        </div>
      )}

      <MemberSendOfferModal userId={memberDetailed.userId} memberId={memberDetailed.id} ref={memberSendOfferModal} />

      <BoxPurchasesCreditModal
        memberId={memberDetailed.id}
        consumableDetails={consumableSelected}
        ref={boxPurchasesCreditModal}
      />

      <BoxSubscriptionDeleteModal
        memberId={memberDetailed.id}
        subscription={subscriptionSelected}
        setSubscriptionSelected={setSubscriptionSelected}
        ref={boxSubscriptionDeleteModal}
      />
    </>
  )
}

function MemberOffersLayoutError({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div className={cn("flex h-80 min-h-full w-full flex-col items-center justify-center gap-2", className)} {...props}>
      <Text fontStyle="BM" className="text-textDefault-body">
        {word("global.error.norep")}
      </Text>
      <img src={errorNoRepLight} />
      <Text fontStyle="BM" className="text-textDefault-caption">
        {word("global.error.somethingGoneWrong")}
      </Text>
    </div>
  )
}
